import { type RefObject, useState } from 'react';
import { motion } from 'framer-motion';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { type ContainerFrequencyOptions } from '@hungryroot/rest';
import {
  Heading,
  Cookie,
  Egg,
  Popcorn,
  Salad,
  Sandwich,
} from '@hungryroot/ui-kit';
import { ControlFrequency } from '../../components/ControlFrequency/ControlFrequency';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { containerFrequencyAnalytics } from '../../segment/quizAnalytics';
import styles from './MealFrequencies.module.css';

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

const mealSections = [
  {
    key: 'breakfastFrequency' as const,
    label: 'Breakfast',
    Icon: Egg,
    analyticsKey: 'breakfast' as const,
  },
  {
    key: 'lunchFrequency' as const,
    label: 'Lunch',
    Icon: Sandwich,
    analyticsKey: 'lunch' as const,
  },
  {
    key: 'dinnerFrequency' as const,
    label: 'Dinner',
    Icon: Salad,
    analyticsKey: 'dinner' as const,
  },
  {
    key: 'snacksFrequency' as const,
    label: 'Snacks',
    Icon: Popcorn,
    analyticsKey: 'snacks' as const,
  },
  {
    key: 'sweetsFrequency' as const,
    label: 'Sweets',
    Icon: Cookie,
    analyticsKey: 'sweets' as const,
  },
];

export function MealFrequencies({ testid, scrollRef }: Props) {
  const [trackedFrequencies, setTrackedFrequencies] = useState<{
    breakfast: ContainerFrequencyOptions | null;
    lunch: ContainerFrequencyOptions | null;
    dinner: ContainerFrequencyOptions | null;
    snacks: ContainerFrequencyOptions | null;
    sweets: ContainerFrequencyOptions | null;
  }>({
    breakfast: null,
    lunch: null,
    dinner: null,
    snacks: null,
    sweets: null,
  });

  useScrollToTopFixIOS(scrollRef);

  const onSubmitAnalytics = () => {
    containerFrequencyAnalytics('breakfast', trackedFrequencies.breakfast);
    containerFrequencyAnalytics('lunch', trackedFrequencies.lunch);
    containerFrequencyAnalytics('dinner', trackedFrequencies.dinner);
    containerFrequencyAnalytics('snacks', trackedFrequencies.snacks);
    containerFrequencyAnalytics('sweets', trackedFrequencies.sweets);
  };

  return (
    <QuizQuestionScreen
      testid={testid}
      heroText="How many days a week should we cover?"
      onSubmitAnalytics={onSubmitAnalytics}
      className={styles['question-screen']}
    >
      <motion.div
        className={styles.container}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut', delay: 0.5 }}
      >
        {mealSections.map(({ key, label, Icon, analyticsKey }) => (
          <div key={key} className={styles.section}>
            <div className={styles.header}>
              <Icon className={styles.icon} />
              <Heading className={styles.heading} variant="H3">
                {label}
              </Heading>
            </div>
            <ControlFrequency
              testid={`${testid}-${analyticsKey}-frequency`}
              question={key}
              type="rounded"
              buttonClassName={styles['button-group']}
              setTrackedFrequency={(value) =>
                setTrackedFrequencies((prev) => ({
                  ...prev,
                  [analyticsKey]: value,
                }))
              }
            />
          </div>
        ))}
      </motion.div>
    </QuizQuestionScreen>
  );
}
